/**
 * Asana Credential Form
 *
 * Unified form supporting two authentication modes:
 * - OAuth2: Client ID + Client Secret → OAuth flow → auto-refreshing tokens
 * - Personal Access Token (PAT): Single access token from Asana Developer Console
 */

import { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { CheckCircle2, XCircle, Loader2, Info, ExternalLink, RefreshCw } from 'lucide-react';
import { credentialsApi } from '@/lib/api';

export interface AsanaOAuthFormData {
  authMode: 'oauth2' | 'pat';
  clientId: string;
  clientSecret: string;
  personalAccessToken: string;

  // System-managed (populated after OAuth)
  accessToken?: string;
  refreshToken?: string;
  tokenExpiresAt?: string;
  tokenType?: string;
  userEmail?: string;
  userName?: string;
  userGid?: string;
}

interface AsanaOAuth2FormProps {
  formData: AsanaOAuthFormData;
  onChange: (data: Partial<AsanaOAuthFormData>) => void;
  isEditing?: boolean;
  credentialId?: string;
  testResult?: { success: boolean; message: string; details?: any } | null;
  testing?: boolean;
  onTest?: () => void;
  /** Which section to render - 'config', 'status', or 'all' */
  section?: 'config' | 'status' | 'all';
}

export function AsanaOAuth2Form({
  formData,
  onChange,
  isEditing = false,
  credentialId,
  testResult,
  testing = false,
  onTest,
  section = 'all',
}: AsanaOAuth2FormProps) {
  const [oauthStatus, setOauthStatus] = useState<any>(null);
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const authMode = formData.authMode || 'oauth2';
  const isPAT = authMode === 'pat';

  const handleChange = (field: keyof AsanaOAuthFormData, value: any) => {
    onChange({ [field]: value });
  };

  useEffect(() => {
    if (isEditing && credentialId && !isPAT) {
      loadOAuthStatus();
    }
  }, [isEditing, credentialId, isPAT]);

  const loadOAuthStatus = async () => {
    if (!credentialId) return;

    setLoadingStatus(true);
    try {
      const response = await credentialsApi.getOAuthStatus(credentialId);
      setOauthStatus(response.data);
    } catch (error) {
      console.error('Failed to load OAuth status:', error);
    } finally {
      setLoadingStatus(false);
    }
  };

  const handleConnectToAsana = async () => {
    if (!credentialId) {
      alert('Please save the credential first before connecting to Asana');
      return;
    }

    try {
      const response = await credentialsApi.getAuthorizationUrl(credentialId);

      const width = 600;
      const height = 700;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;

      const popup = window.open(
        response.data.authorizationUrl,
        'Asana OAuth',
        `width=${width},height=${height},left=${left},top=${top},toolbar=no,location=no,status=no,menubar=no,scrollbars=yes`
      );

      if (!popup) {
        alert('Popup blocked! Please allow popups for this site and try again.');
        return;
      }

      const messageHandler = (event: MessageEvent) => {
        if (event.data?.type === 'oauth-callback') {
          window.removeEventListener('message', messageHandler);
          loadOAuthStatus();
        }
      };
      window.addEventListener('message', messageHandler);

      const pollTimer = setInterval(() => {
        if (popup.closed) {
          clearInterval(pollTimer);
          window.removeEventListener('message', messageHandler);
          loadOAuthStatus();
        }
      }, 500);

    } catch (error: any) {
      alert(`Failed to get authorization URL: ${error.response?.data?.error || error.message}`);
    }
  };

  const handleRefreshToken = async () => {
    if (!credentialId) return;

    setRefreshing(true);
    try {
      await credentialsApi.refreshOAuthToken(credentialId, 'asana');
      await loadOAuthStatus();
      alert('Token refreshed successfully');
    } catch (error: any) {
      alert(`Failed to refresh token: ${error.response?.data?.error || error.message}`);
    } finally {
      setRefreshing(false);
    }
  };

  const isAuthorized = oauthStatus?.authorized || !!formData.accessToken;
  const isExpired = oauthStatus?.isExpired || false;
  const tokenExpiresAt = oauthStatus?.expiresAt || formData.tokenExpiresAt;

  // Auth mode selector
  const authModeSelector = (
    <div className="space-y-2">
      <Label className="text-sm font-medium">Authentication Method</Label>
      <div className="flex gap-2">
        <Button
          type="button"
          variant={!isPAT ? 'default' : 'outline'}
          size="sm"
          className="flex-1"
          onClick={() => handleChange('authMode', 'oauth2')}
        >
          OAuth2
        </Button>
        <Button
          type="button"
          variant={isPAT ? 'default' : 'outline'}
          size="sm"
          className="flex-1"
          onClick={() => handleChange('authMode', 'pat')}
        >
          Personal Access Token
        </Button>
      </div>
    </div>
  );

  // PAT config
  const patConfigContent = (
    <div className="space-y-4">
      {authModeSelector}

      <div className="space-y-2">
        <Label htmlFor="personalAccessToken" className="text-sm font-medium">
          Access Token <span className="text-red-500">*</span>
        </Label>
        <Input
          id="personalAccessToken"
          type="password"
          placeholder="Enter your Asana Personal Access Token"
          value={formData.personalAccessToken}
          onChange={(e) => handleChange('personalAccessToken', e.target.value)}
          className="font-mono text-sm"
        />
      </div>

      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription>
          Generate a Personal Access Token at{' '}
          <a
            href="https://app.asana.com/0/developer-console"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:underline"
          >
            Asana Developer Console
          </a>
          {' '}under your profile settings. PATs do not expire but can be revoked.
        </AlertDescription>
      </Alert>
    </div>
  );

  // OAuth2 config
  const oauthConfigContent = (
    <div className="space-y-4">
      {authModeSelector}

      <div className="space-y-2">
        <Label htmlFor="clientId" className="text-sm font-medium">
          Client ID <span className="text-red-500">*</span>
        </Label>
        <Input
          id="clientId"
          placeholder="Enter your Asana app Client ID"
          value={formData.clientId}
          onChange={(e) => handleChange('clientId', e.target.value)}
          className="font-mono text-sm"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="clientSecret" className="text-sm font-medium">
          Client Secret <span className="text-red-500">*</span>
        </Label>
        <Input
          id="clientSecret"
          type="password"
          placeholder="Enter your Asana app Client Secret"
          value={formData.clientSecret}
          onChange={(e) => handleChange('clientSecret', e.target.value)}
          className="font-mono text-sm"
        />
      </div>

      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription>
          Create an app at{' '}
          <a
            href="https://app.asana.com/0/developer-console"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:underline"
          >
            Asana Developer Console
          </a>
          {' '}to get your Client ID and Client Secret.
        </AlertDescription>
      </Alert>
    </div>
  );

  const configContent = isPAT ? patConfigContent : oauthConfigContent;

  // PAT status (simple — just shows if token is set)
  const patStatusContent = (
    <div className="space-y-4">
      <div className="rounded-lg border p-4 space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium">Authentication Status</span>
          {formData.personalAccessToken ? (
            <div className="flex items-center gap-2 text-green-600">
              <CheckCircle2 className="h-4 w-4" />
              <span className="text-sm font-medium">Token Configured</span>
            </div>
          ) : (
            <div className="flex items-center gap-2 text-gray-500">
              <XCircle className="h-4 w-4" />
              <span className="text-sm font-medium">No Token</span>
            </div>
          )}
        </div>
      </div>

      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription>
          Personal Access Tokens don't expire and don't require an OAuth flow.
          Save the credential with your token to start using the Asana node.
        </AlertDescription>
      </Alert>
    </div>
  );

  // OAuth2 status
  const oauthStatusContent = (
    <div className="space-y-4">
      {loadingStatus ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-8 w-8 animate-spin text-pink-600" />
        </div>
      ) : (
        <>
          <div className="rounded-lg border p-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Authorization Status</span>
              {isAuthorized ? (
                <div className="flex items-center gap-2 text-green-600">
                  <CheckCircle2 className="h-4 w-4" />
                  <span className="text-sm font-medium">Connected</span>
                </div>
              ) : (
                <div className="flex items-center gap-2 text-gray-500">
                  <XCircle className="h-4 w-4" />
                  <span className="text-sm font-medium">Not Connected</span>
                </div>
              )}
            </div>

            {isAuthorized && (
              <>
                {oauthStatus?.userEmail && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Connected Account</span>
                    <span className="font-mono">{oauthStatus.userEmail}</span>
                  </div>
                )}

                {oauthStatus?.userName && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Display Name</span>
                    <span>{oauthStatus.userName}</span>
                  </div>
                )}

                {tokenExpiresAt && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Token Expires</span>
                    <span className={isExpired ? 'text-red-600 font-medium' : ''}>
                      {new Date(tokenExpiresAt).toLocaleString()}
                      {isExpired && ' (Expired)'}
                    </span>
                  </div>
                )}

                {oauthStatus?.hasRefreshToken && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Refresh Token</span>
                    <span className="text-green-600">Available</span>
                  </div>
                )}
              </>
            )}
          </div>

          {!isAuthorized && (
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription>
                Save the credential first, then click "Connect to Asana" to authorize access.
              </AlertDescription>
            </Alert>
          )}

          {isExpired && (
            <Alert className="bg-amber-50 border-amber-200">
              <Info className="h-4 w-4 text-amber-600" />
              <AlertDescription className="text-amber-900">
                Your access token has expired. Click "Refresh Token" to renew it.
              </AlertDescription>
            </Alert>
          )}

          <div className="flex gap-2">
            <Button
              onClick={handleConnectToAsana}
              disabled={!credentialId || !formData.clientId}
              className="flex-1"
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              {isAuthorized ? 'Reconnect to Asana' : 'Connect to Asana'}
            </Button>

            {isAuthorized && (
              <Button
                onClick={handleRefreshToken}
                disabled={refreshing || !oauthStatus?.hasRefreshToken}
                variant="outline"
              >
                {refreshing ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}
              </Button>
            )}
          </div>
        </>
      )}
    </div>
  );

  const statusContent = isPAT ? patStatusContent : oauthStatusContent;

  if (section === 'config') {
    return configContent;
  }

  if (section === 'status') {
    return statusContent;
  }

  return (
    <div className="space-y-6">
      {configContent}
      <hr />
      {statusContent}
    </div>
  );
}
